<template>
  <div>
    <div class="float-right">
      <FormInputText placeholder="Search" v-model="searchText" icon="fa-search" class="d-inline-block mr-2" />
      <FormButton class="btn-warning btn-xs mr-2" @click="clearAllocations"><i class="far fa-trash-alt fa-fw"></i> Clear Allocations</FormButton>
      <FormButton class="btn-primary btn-xs" @click="autoAllocation"><i class="far fa-magic fa-fw"></i> Auto Allocate</FormButton>
    </div>
    <ContentTitle>Order Allocations</ContentTitle>
    <ContentSubTitle>{{availableOrderList.length}} order{{availableOrderList.length | displayCountPlural}} available for payment</ContentSubTitle>

    <div class="order-list-scroll">
      <StaticDataTable class="static-table-list">
        <template v-slot:header>
          <tr>
            <th width="8%">Order</th>
            <th width="14%">Status</th>
            <th width="8%">Reference</th>
            <th width="10%">Date</th>
            <th width="10%">Total</th>
            <th width="10%">Amount Due</th>
            <th width="25%">Allocation</th>
            <th width="10%">Remaining</th>
            <th width="5%">Actions</th>
          </tr>
        </template>
        <template v-slot:body>
          <tr v-for="order in filteredOrderList" :key="order.id" :class="{'row-hightlight': order.id == defaultOrderId}">
            <td class="font-weight-bold">
              <LinkOrder :id="order.id" :name="`#${order.sequenceNumber}`" />
            </td>
            <td>
              <LabelOrderStatusBadge
                :status="order.status"
                :isOnHold="order.isOnHold"
                :holdType="order.holdType"
                :isUrgent="order.isUrgent"
                :isInvoiced="order.isInvoiced"
                :isInvoicingComplete="order.isInvoicingComplete" />
            </td>
            <td>
              {{order.customerRef | formatHyphenEmptyValue}}
            </td>
            <td>
              {{order.dateOrderCreated | formatDate}}
            </td>            
            <td>
              {{order.totalPrice | formatCurrency}}
            </td>
            <td>
              {{order.amountDue | formatCurrency}}
            </td>
            <td>
              <FormInputCurrency v-model="order.allocatedAmount" @input="orderListUpdated()" />
            </td>
            <td>
              <span :class="{'result-fg-success': isOrderPaidOff(order), 'result-fg-failed': isOrderInvalidAmount(order)}">{{order.amountDue - order.allocatedAmount | formatCurrency}}</span>
              <Icon icon="fa-check-circle" iconStyle="fas" iconClass="result-fg-success" v-if="isOrderPaidOff(order)" class="ml-1" />
              <Icon icon="fa-exclamation-circle" iconStyle="fas" iconClass="result-fg-failed" v-if="isOrderInvalidAmount(order)" class="ml-1" />
            </td>
            <td>
              <ActionDropMenu :tableAction="true">
                <DropdownMenuItem v-if="!isFixedAmount" @click="addOrderToPayment(order)" icon="fa-usd-circle">Increase Payment</DropdownMenuItem>
                <DropdownMenuDivider v-if="!isFixedAmount" />
                <DropdownMenuItem @click="addOrderAllocation(order)" icon="fa-plus-circle">Allocate</DropdownMenuItem>
                <DropdownMenuItem @click="clearOrderAllocation(order)" icon="fa-minus-circle">Clear</DropdownMenuItem>
              </ActionDropMenu>
            </td>
          </tr>
        </template>
        <template v-slot:empty v-if="availableOrderList.length == 0">No orders waiting payment found</template>
      </StaticDataTable>
    </div>
    <StaticDataTable class="static-table-list">
      <template v-slot:header>
        <tr class="border-top">
          <th width="8%">Totals</th>
          <th width="22%" colspan="2">{{availableOrderList.length}} order{{availableOrderList.length | displayCountPlural}}</th>
          <th width="70%"> </th>
        </tr>
      </template>
    </StaticDataTable>
  </div>
</template>

<style scoped>
.order-list-scroll {
  height: 250px;
  overflow-y: scroll;
}
</style>

<script>
import _ from 'lodash'
import Utils from '@/utils/utils'

import LabelOrderStatusBadge from '@/modules/orders/components/labelOrderStatusBadge'

import { OrderStatusTypes, OrderStatusTypeLookup } from '@/store/types'

export default {
  name: 'OrderAllocationList',
  model: {
    event: 'update'
  },
  components: {
    LabelOrderStatusBadge
  },
  props: {
    contactId: Number,
    defaultOrderId: Number,
    paymentAmount: Number,
    isFixedAmount: Boolean,
    value: Array
  },
  data() {
    return {
      availableOrderList: [],
      loading: false,
      loadingError: '',
      searchText: ''
    }
  },
  computed: {
    currentPayment() {
      return this.parseDecimal(this.paymentAmount)
    },
    totalOrderAllocation() {
      return this.availableOrderList.reduce((curVal, order) => {
        return Utils.roundCurrency(curVal + this.parseDecimal(order.allocatedAmount))
      }, 0)
    },
    filteredOrderList() {
      if(!this.searchText)
        return this.availableOrderList

      let searchText = (this.searchText+'').trim().toLowerCase()

      return this.availableOrderList.filter(order => {
        if(order.sequenceNumber && order.sequenceNumber.toLowerCase().includes(searchText))
          return true
        if(order.customerRef && order.customerRef.toLowerCase().includes(searchText))
          return true
        if((order.totalPrice+'').includes(searchText))
          return true
        if((order.amountDue+'').includes(searchText))
          return true
        return false
      })
    }
  },
  watch: {
    contactId: {
      immediate: true,
      handler: function() {
        this.requestOrderList()
      }
    },
    value: function() {
      if(this.value) {
        this.availableOrderList = _.cloneDeep(this.value)
      }
    }
  },
  created() {
    this.OrderStatusTypeLookup = OrderStatusTypeLookup
  },
  methods: {
    requestOrderList() {
      if(this.contactId == null) {
        this.availableOrderList = []
        this.orderListUpdated()
        return
      }
      // Get the list of orders for this contact available for payment
      this.loading = true
      this.loadingError = ''
      this.availableOrderList = []

      // Orders in any of the following statuses can have money allocated, due oldest first
      var options = {
        filter: { contactId: this.contactId, status: [OrderStatusTypes.pendingPrePaid, OrderStatusTypes.pendingExternalSetup, OrderStatusTypes.preProduction, OrderStatusTypes.production, OrderStatusTypes.pendingCODPaid, OrderStatusTypes.despatch].join(',') },
        order: [{ column: 0, dir: 'ASC' }],
        columns: [{ data: 'dateOrderCreated' }],
        viewType: 'active'    // ActiveOrder view. Has extended details about active orders, including accounts info
      }

      this.$apiService.ordersService.getOrderFilteredList(options)
      .then(response => {

        this.availableOrderList = response.data.map(order => {
          return {
            id: order.id,
            status: order.status,
            sequenceNumber: order.sequenceNumber,
            dateOrderCreated: order.dateOrderCreated,
            customerRef: order.customerRef,
            totalPrice: order.totalPrice,
            amountDue: Utils.roundCurrency(order.totalOwingAmount),
            allocatedAmount: 0,
            isOnHold: order.isOnHold,
            isUrgent: order.isUrgent,
            holdType: order.holdType,
            isInvoicingComplete: order.isInvoicingComplete
          }
        })

        // Ignore orders not requiring payment (unless order selected specifically)
        this.availableOrderList = this.availableOrderList.filter(order => {
          if(order.amountDue <= 0 && order.id != this.defaultOrderId)
            return false
          return true
        })
        
        // If orderId set, show it at the top of the list
        // Auto pay the selected order
        if(this.defaultOrderId) {
          let selectedOrder = this.availableOrderList.find(order => order.id == this.defaultOrderId)
          if(selectedOrder) {
            this.availableOrderList = _.concat(selectedOrder, this.availableOrderList.filter(order => order.id != this.defaultOrderId))
            
            this.availableOrderList[0].allocatedAmount = selectedOrder.amountDue
            this.$emit('updatePayment', selectedOrder.amountDue)
          }
        }
      })
      .catch(err => {
        this.loadingError = this.processFormResponse(err).errorMessage
      })
      .finally(() => {
        this.loading = false
        this.orderListUpdated()
      })
    },
    isOrderPaidOff(order) {
      return Utils.roundCurrency(order.amountDue - this.parseDecimal(order.allocatedAmount)) == 0
    },
    isOrderInvalidAmount(order) {
      return Utils.roundCurrency(order.amountDue - this.parseDecimal(order.allocatedAmount)) < 0
    },
    clearAllocations() {
      this.availableOrderList.forEach(order => {
        order.allocatedAmount = 0
      })
      this.orderListUpdated()
    },
    autoAllocation() {
      var amount = this.paymentAmount

      this.availableOrderList.forEach(order => {
        if(amount > 0) {
          let allocateAmount = Utils.roundCurrency(Math.min(order.amountDue, amount))
          order.allocatedAmount = allocateAmount
          amount -= allocateAmount
        }
      })
      this.orderListUpdated()
    },
    clearOrderAllocation(order) {
      order.allocatedAmount = 0
      this.orderListUpdated()
    },
    addOrderAllocation(order) {
      let availableAmount = this.paymentAmount
      if(availableAmount > 0) {
        order.allocatedAmount = Utils.roundCurrency(Math.min(order.amountDue, availableAmount))
        this.orderListUpdated()
      }
    },
    addOrderToPayment(order) {
      var newPaymentAmount = this.currentPayment

      // Make sure we have enough for current allocations
      if(this.currentPayment - this.totalOrderAllocation < 0)
        newPaymentAmount = Utils.roundCurrency(this.totalOrderAllocation)

      // Add to the payment to pay this order
      let requiredAmount = Math.max(order.amountDue - this.parseDecimal(order.allocatedAmount), 0)
      let availablePayment = Math.max(newPaymentAmount - this.totalOrderAllocation, 0)
      let additionalPayment = requiredAmount - availablePayment

      if(additionalPayment > 0)
        newPaymentAmount = Utils.roundCurrency(newPaymentAmount + additionalPayment)
      
      order.allocatedAmount = order.amountDue
      this.$emit('updatePayment', newPaymentAmount)
      this.orderListUpdated()
    },
    orderListUpdated() {
      this.$emit('update', this.availableOrderList)
    }
  }
}
</script>